import { BrowserRouter } from 'react-router'
import type { JSX, ReactNode } from 'react'
import { AuthProvider } from '@/auth/AuthProvider'
import { TenantProvider } from '@/tenant/TenantProvider'
import { ToastProvider } from '@/ui/ToastProvider'
import { AppRoutes } from '@/routes'

/**
 * The tenant app.
 *
 * Provider order is load-bearing. `TenantProvider` reads the session from
 * `AuthProvider` to know which organisation it is in, so it must be inside it.
 * `ToastProvider` is outermost, so a toast raised on the way out of a screen
 * is not unmounted by the navigation that raised it.
 *
 * `Providers` is exported separately so tests can mount the same tree around a
 * `MemoryRouter`: a test that assembles its own provider stack proves the stack
 * it assembled, not the one that ships.
 */
export function Providers({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ToastProvider>
      <AuthProvider>
        <TenantProvider>{children}</TenantProvider>
      </AuthProvider>
    </ToastProvider>
  )
}

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <Providers>
        <AppRoutes />
      </Providers>
    </BrowserRouter>
  )
}
